import React, { useState, useEffect } from "react";
import { useAuth, useBusiness, api } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const Settings = () => {
  const { user } = useAuth();
  const { businesses, selectedBusiness, updateBusiness, deleteBusiness, fetchBusinesses } = useBusiness();
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [saving, setSaving] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [applyingIndustry, setApplyingIndustry] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [emailForm, setEmailForm] = useState({
    recipient_email: "",
    recipient_name: "",
    message: "",
    format: "pdf",
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedBusinessForEmail, setSelectedBusinessForEmail] = useState(null);
  
  // Reminder preferences
  const [reminderPrefs, setReminderPrefs] = useState({
    email_enabled: true,
    sms_enabled: false,
    phone_number: "",
    reminder_day_before: true,
    reminder_morning_of: true
  });
  const [savingReminders, setSavingReminders] = useState(false);
  const [testingReminders, setTestingReminders] = useState(false);

  // Delete account state
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);

  // Disconnect bank state
  const [connectedBanks, setConnectedBanks] = useState([]);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [disconnectingBank, setDisconnectingBank] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMfaDisable, setShowMfaDisable] = useState(false);
  const [mfaDisablePassword, setMfaDisablePassword] = useState("");

  useEffect(() => {
    fetchReminderPreferences();
    fetchConnectedBanks();
    fetchMfaStatus();
    api.get("/industries").then(r => setIndustries(r.data)).catch(() => {});
  }, []);

  const fetchConnectedBanks = async () => {
    try {
      const response = await api.get("/account/connected-banks");
      setConnectedBanks(response.data);
    } catch (error) {
      console.error("Failed to fetch connected banks:", error);
    }
  };

  const fetchMfaStatus = async () => {
    try {
      const response = await api.get("/mfa/status");
      setMfaEnabled(response.data.mfa_enabled);
    } catch (error) {
      console.error("Failed to fetch MFA status:", error);
    }
  };

  const handleMfaSetup = async () => {
    setMfaLoading(true);
    try {
      const response = await api.get("/mfa/setup");
      setMfaSetupData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start MFA setup");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaEnable = async () => {
    if (mfaCode.length !== 6) {
      toast.error("Enter the 6-digit code from your authenticator app");
      return;
    }
    setMfaLoading(true);
    try {
      await api.post("/mfa/enable", { code: mfaCode });
      setMfaEnabled(true);
      setMfaSetupData(null);
      setMfaCode("");
      toast.success("Two-factor authentication enabled!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaDisable = async () => {
    if (!mfaDisablePassword) {
      toast.error("Enter your password to disable MFA");
      return;
    }
    setMfaLoading(true);
    try {
      await api.post("/mfa/disable", { password: mfaDisablePassword });
      setMfaEnabled(false);
      setShowMfaDisable(false);
      setMfaDisablePassword("");
      toast.success("Two-factor authentication disabled");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disable MFA");
    } finally {
      setMfaLoading(false);
    }
  };


  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error("Please enter your password to confirm");
      return;
    }
    setDeletingAccount(true);
    try {
      const response = await api.post("/account/delete", {
        password: deletePassword,
        reason: deleteReason,
      });
      toast.success("Account deleted successfully");
      setShowDeleteAccount(false);
      // Log out
      setTimeout(() => {
        localStorage.clear();
        window.location.href = "/";
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleDisconnectBank = async () => {
    if (!disconnectingBank) return;
    setDisconnecting(true);
    try {
      await api.post("/account/disconnect-bank", {
        account_id: disconnectingBank.id,
      });
      toast.success("Bank disconnected successfully");
      setShowDisconnectDialog(false);
      setDisconnectingBank(null);
      fetchConnectedBanks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disconnect bank");
    } finally {
      setDisconnecting(false);
    }
  };

  const fetchReminderPreferences = async () => {
    try {
      const response = await api.get("/reminders/preferences");
      setReminderPrefs(response.data);
    } catch (error) {
      console.error("Failed to fetch reminder preferences:", error);
    }
  };

  const handleSaveReminders = async () => {
    setSavingReminders(true);
    try {
      await api.put("/reminders/preferences", reminderPrefs);
      toast.success("Reminder preferences saved!");
    } catch (error) {
      toast.error("Failed to save reminder preferences");
    } finally {
      setSavingReminders(false);
    }
  };

  const handleTestReminders = async () => {
    setTestingReminders(true);
    try {
      const response = await api.post("/reminders/send-test");
      if (response.data.email === "sent" || response.data.sms === "sent") {
        toast.success("Test reminder sent! Check your email/phone.");
      } else {
        toast.info(`Email: ${response.data.email}, SMS: ${response.data.sms}`);
      }
    } catch (error) {
      toast.error("Failed to send test reminder");
    } finally {
      setTestingReminders(false);
    }
  };

  const handleUpdateBusiness = async (e) => {
    e.preventDefault();
    const total = editingBusiness.profit_percent + editingBusiness.tax_percent +
                  editingBusiness.owner_pay_percent + editingBusiness.operating_expense_percent;
    
    if (Math.abs(total - 100) > 0.01) {
      toast.error(`Percentages must sum to 100%. Current total: ${total}%`);
      return;
    }

    setSaving(true);
    try {
      await updateBusiness(editingBusiness.id, {
        name: editingBusiness.name,
        profit_percent: editingBusiness.profit_percent,
        tax_percent: editingBusiness.tax_percent,
        owner_pay_percent: editingBusiness.owner_pay_percent,
        operating_expense_percent: editingBusiness.operating_expense_percent,
      });
      toast.success("Business updated successfully!");
      setEditingBusiness(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update business");
    } finally {
      setSaving(false);
    }
  };

  const handleChangeIndustry = async (businessId, industryId) => {
    setApplyingIndustry(true);
    try {
      await api.post(`/industries/apply/${businessId}`, { industry_id: industryId });
      toast.success("Industry updated! Categories, rules & deductions have been refreshed.");
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply industry");
    }
    setApplyingIndustry(false);
  };

  const handleDeleteBusiness = async (businessId, businessName) => {
    if (deleteConfirm !== businessName) {
      toast.error("Please type the business name to confirm deletion");
      return;
    }

    try {
      await deleteBusiness(businessId);
      toast.success("Business deleted successfully!");
      setDeleteConfirm("");
    } catch (error) {
      toast.error("Failed to delete business");
    }
  };

  const handleExport = async (businessId, businessName, format) => {
    setExporting(true);
    try {
      const endpoint = format === "pdf" 
        ? `/export/pdf/${businessId}` 
        : `/export/cpa-spreadsheet/${businessId}`;
      
      const response = await api.get(endpoint, {
        responseType: "blob",
      });
      
      const extension = format === "pdf" ? "pdf" : "csv";
      const mimeType = format === "pdf" ? "application/pdf" : "text/csv";
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `ProfitFirst_Report_${businessName.replace(/ /g, "_")}_${new Date().toISOString().split("T")[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} report downloaded!`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(`Failed to export ${format.toUpperCase()} report`);
    } finally {
      setExporting(false);
    }
  };

  const handleEmailReport = async () => {
    if (!emailForm.recipient_email || !emailForm.recipient_name) {
      toast.error("Please fill in recipient email and name");
      return;
    }

    setSendingEmail(true);
    try {
      await api.post("/export/email", {
        business_id: selectedBusinessForEmail.id,
        recipient_email: emailForm.recipient_email,
        recipient_name: emailForm.recipient_name,
        message: emailForm.message,
        format: emailForm.format,
      });
      toast.success(`Report will be sent to ${emailForm.recipient_email}`);
      setShowEmailDialog(false);
      setEmailForm({ recipient_email: "", recipient_name: "", message: "", format: "pdf" });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send email";
      if (errorMsg.includes("not configured")) {
        toast.error("Email not configured. Please add SMTP settings to send emails.");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-stone-900">Settings</h1>
        <p className="text-stone-600">Manage your account and business settings</p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
              <span className="text-2xl font-bold text-[#1e3a5f]">
                {user?.full_name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-lg font-semibold text-stone-900">{user?.full_name}</p>
              <p className="text-stone-600">{user?.email}</p>
            </div>
          </div>
          <Separator />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-stone-500">Account ID</Label>
              <p className="text-sm font-mono text-stone-700">{user?.id?.slice(0, 8)}...</p>
            </div>
            <div>
              <Label className="text-xs text-stone-500">Member Since</Label>
              <p className="text-sm text-stone-700">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card data-testid="mfa-settings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security with an authenticator app (Google Authenticator, Authy, etc.)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mfaEnabled ? (
            <>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
                </div>
                <div>
                  <p className="font-medium text-emerald-900" data-testid="mfa-status-enabled">MFA is enabled</p>
                  <p className="text-xs text-emerald-600">Your account is protected with two-factor authentication</p>
                </div>
              </div>
              {!showMfaDisable ? (
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setShowMfaDisable(true)}
                  data-testid="mfa-disable-start-btn"
                >
                  Disable MFA
                </Button>
              ) : (
                <div className="space-y-3 p-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700 font-medium">Enter your password to disable MFA</p>
                  <Input
                    type="password"
                    placeholder="Your password"
                    value={mfaDisablePassword}
                    onChange={e => setMfaDisablePassword(e.target.value)}
                    data-testid="mfa-disable-password-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleMfaDisable}
                      disabled={mfaLoading}
                      data-testid="mfa-disable-confirm-btn"
                    >
                      {mfaLoading ? "Disabling..." : "Confirm Disable"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setShowMfaDisable(false); setMfaDisablePassword(""); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : mfaSetupData ? (
            <div className="space-y-4">
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <p className="text-sm text-stone-600 mb-3">Scan this QR code with your authenticator app:</p>
                <img
                  src={mfaSetupData.qr_code}
                  alt="MFA QR Code"
                  className="mx-auto w-48 h-48 rounded-lg border"
                  data-testid="mfa-qr-code"
                />
                <div className="mt-3">
                  <p className="text-[11px] text-stone-400 mb-1">Or enter this key manually:</p>
                  <code className="text-xs bg-white px-3 py-1.5 rounded border font-mono select-all" data-testid="mfa-secret-key">
                    {mfaSetupData.secret}
                  </code>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Enter the 6-digit code from your app to verify:</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="text-center text-lg font-mono tracking-[0.5em] h-12"
                  data-testid="mfa-setup-code-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  className="bg-[#1e3a5f] hover:bg-[#2d5a8e]"
                  onClick={handleMfaEnable}
                  disabled={mfaLoading || mfaCode.length !== 6}
                  data-testid="mfa-enable-confirm-btn"
                >
                  {mfaLoading ? "Verifying..." : "Enable MFA"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setMfaSetupData(null); setMfaCode(""); }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-amber-900">MFA is not enabled</p>
                  <p className="text-xs text-amber-600">Protect your account with two-factor authentication</p>
                </div>
              </div>
              <Button
                className="bg-[#1e3a5f] hover:bg-[#2d5a8e]"
                size="sm"
                onClick={handleMfaSetup}
                disabled={mfaLoading}
                data-testid="mfa-setup-btn"
              >
                {mfaLoading ? "Loading..." : "Set Up MFA"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>


      {/* CPA/Accountant Export */}
      <Card className="bg-gradient-to-br from-blue-50 to-stone-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-800 flex items-center gap-2">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
            </svg>
            Export for CPA / Accountant
          </CardTitle>
          <CardDescription>
            Generate professional reports in PDF or CSV format, or email directly to your accountant
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-white border border-blue-100">
              <h4 className="font-medium text-stone-900 mb-2">What's included in the export:</h4>
              <ul className="grid grid-cols-2 gap-2 text-sm text-stone-600">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Business information & settings
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  All revenue allocations
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Quarterly reviews & distributions
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Profit & Loss statements
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Reserve runway status
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Cash flow projections
                </li>
              </ul>
            </div>
            
            {businesses.length > 0 ? (
              <div className="space-y-3">
                <Label>Select business and export format:</Label>
                {businesses.map(business => (
                  <div key={business.id} className="p-4 rounded-lg border border-stone-200 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium text-stone-900">{business.name}</p>
                        <p className="text-xs text-stone-500">
                          Profit {business.profit_percent}% | Tax {business.tax_percent}% | Owner {business.owner_pay_percent}% | OpEx {business.operating_expense_percent}%
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => handleExport(business.id, business.name, "pdf")}
                        disabled={exporting}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid={`export-pdf-${business.id}`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                        Download PDF
                      </Button>
                      <Button
                        onClick={() => handleExport(business.id, business.name, "csv")}
                        disabled={exporting}
                        variant="outline"
                        className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f]/5"
                        data-testid={`export-csv-${business.id}`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                        </svg>
                        Download CSV
                      </Button>
                      <Dialog open={showEmailDialog && selectedBusinessForEmail?.id === business.id} onOpenChange={(open) => {
                        setShowEmailDialog(open);
                        if (open) setSelectedBusinessForEmail(business);
                      }}>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            className="border-blue-600 text-blue-600 hover:bg-blue-50"
                            data-testid={`email-report-${business.id}`}
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                            </svg>
                            Email to CPA
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Email Report to CPA/Accountant</DialogTitle>
                            <DialogDescription>
                              Send the financial report for {business.name} directly to your accountant
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div className="space-y-2">
                              <Label htmlFor="recipient_name">Recipient Name</Label>
                              <Input
                                id="recipient_name"
                                placeholder="John Smith, CPA"
                                value={emailForm.recipient_name}
                                onChange={(e) => setEmailForm({...emailForm, recipient_name: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="recipient_email">Recipient Email</Label>
                              <Input
                                id="recipient_email"
                                type="email"
                                placeholder="cpa@example.com"
                                value={emailForm.recipient_email}
                                onChange={(e) => setEmailForm({...emailForm, recipient_email: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="format">Report Format</Label>
                              <Select value={emailForm.format} onValueChange={(value) => setEmailForm({...emailForm, format: value})}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pdf">PDF (Professional Report)</SelectItem>
                                  <SelectItem value="csv">CSV (Spreadsheet)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="message">Personal Message (optional)</Label>
                              <Textarea
                                id="message"
                                placeholder="Hi, please find attached my quarterly financial report..."
                                value={emailForm.message}
                                onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                                rows={3}
                              />
                            </div>
                            <Button
                              onClick={handleEmailReport}
                              disabled={sendingEmail}
                              className="w-full bg-blue-600 hover:bg-blue-700"
                            >
                              {sendingEmail ? "Sending..." : "Send Report"}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">
                Create a business first to export data.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Businesses */}
      <Card>
        <CardHeader>
          <CardTitle>Your Businesses</CardTitle>
          <CardDescription>Manage Opturna settings for each business</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {businesses.length > 0 ? (
            businesses.map(business => (
              <div key={business.id} className="p-4 rounded-xl border border-stone-200 bg-stone-50">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-stone-900">{business.name}</h3>
                    {selectedBusiness?.id === business.id && (
                      <span className="text-xs text-[#1e3a5f] font-medium">Currently Selected</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingBusiness({ ...business })}
                          data-testid={`edit-business-${business.id}`}
                        >
                          Edit
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Edit Business</DialogTitle>
                          <DialogDescription>
                            Update your business name and allocation percentages
                          </DialogDescription>
                        </DialogHeader>
                        {editingBusiness && (
                          <form onSubmit={handleUpdateBusiness} className="space-y-4 mt-4">
                            <div className="space-y-2">
                              <Label htmlFor="edit-name">Business Name</Label>
                              <Input
                                id="edit-name"
                                value={editingBusiness.name}
                                onChange={(e) => setEditingBusiness({
                                  ...editingBusiness,
                                  name: e.target.value
                                })}
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Industry</Label>
                              <div className="flex gap-2">
                                <select
                                  value={editingBusiness.industry || ""}
                                  onChange={(e) => setEditingBusiness({ ...editingBusiness, industry: e.target.value })}
                                  className="flex-1 px-3 py-2 rounded-md border border-[#e4e8ee] text-sm focus:outline-none focus:ring-1 focus:ring-[#1e3a5f]/20"
                                  data-testid="edit-industry-select"
                                >
                                  <option value="">Select industry...</option>
                                  {industries.map(ind => (
                                    <option key={ind.id} value={ind.id}>{ind.name}</option>
                                  ))}
                                </select>
                                <Button
                                  type="button"
                                  size="sm"
                                  disabled={!editingBusiness.industry || applyingIndustry}
                                  onClick={() => handleChangeIndustry(editingBusiness.id, editingBusiness.industry)}
                                  className="bg-[#1e3a5f] hover:bg-[#2d5a8e] whitespace-nowrap"
                                  data-testid="apply-industry-btn"
                                >
                                  {applyingIndustry ? "Applying..." : "Apply"}
                                </Button>
                              </div>
                              <p className="text-[10px] text-[#8b95a5]">Changing industry refreshes categories, rules & deduction recommendations</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Profit %</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={editingBusiness.profit_percent}
                                  onChange={(e) => setEditingBusiness({
                                    ...editingBusiness,
                                    profit_percent: parseFloat(e.target.value) || 0
                                  })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Tax %</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={editingBusiness.tax_percent}
                                  onChange={(e) => setEditingBusiness({
                                    ...editingBusiness,
                                    tax_percent: parseFloat(e.target.value) || 0
                                  })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Owner Pay %</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={editingBusiness.owner_pay_percent}
                                  onChange={(e) => setEditingBusiness({
                                    ...editingBusiness,
                                    owner_pay_percent: parseFloat(e.target.value) || 0
                                  })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Operating %</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={editingBusiness.operating_expense_percent}
                                  onChange={(e) => setEditingBusiness({
                                    ...editingBusiness,
                                    operating_expense_percent: parseFloat(e.target.value) || 0
                                  })}
                                />
                              </div>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Total: {(
                                editingBusiness.profit_percent +
                                editingBusiness.tax_percent +
                                editingBusiness.owner_pay_percent +
                                editingBusiness.operating_expense_percent
                              ).toFixed(1)}%
                              <span className="text-destructive"> (must equal 100%)</span>
                            </div>
                            <Button
                              type="submit"
                              disabled={saving}
                              className="w-full bg-[#1e3a5f] hover:bg-[#2d5a8e]"
                            >
                              {saving ? "Saving..." : "Save Changes"}
                            </Button>
                          </form>
                        )}
                      </DialogContent>
                    </Dialog>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-business-${business.id}`}
                        >
                          Delete
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-red-600">Delete Business</DialogTitle>
                          <DialogDescription>
                            This action cannot be undone. All allocation history for this business will be lost.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                            <p className="text-sm text-red-800">
                              Type <strong>{business.name}</strong> to confirm deletion:
                            </p>
                          </div>
                          <Input
                            value={deleteConfirm}
                            onChange={(e) => setDeleteConfirm(e.target.value)}
                            placeholder={business.name}
                          />
                          <Button
                            variant="destructive"
                            className="w-full"
                            onClick={() => handleDeleteBusiness(business.id, business.name)}
                            disabled={deleteConfirm !== business.name}
                          >
                            Delete Business
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Allocation Percentages */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-3 rounded-lg bg-white">
                    <p className="text-lg font-bold text-[#1e3a5f]">{business.profit_percent}%</p>
                    <p className="text-xs text-stone-500">Profit</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-white">
                    <p className="text-lg font-bold text-[#1e3a5f]">{business.tax_percent}%</p>
                    <p className="text-xs text-stone-500">Tax</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-white">
                    <p className="text-lg font-bold text-[#3d7abd]">{business.owner_pay_percent}%</p>
                    <p className="text-xs text-stone-500">Owner Pay</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-white">
                    <p className="text-lg font-bold text-[#6b9fd4]">{business.operating_expense_percent}%</p>
                    <p className="text-xs text-stone-500">Operating</p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No businesses yet. Go to Dashboard to create one.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Allocation Reminders */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>🔔</span> Allocation Reminders
          </CardTitle>
          <CardDescription>
            Get reminded on the 9th/24th (day before) and 10th/25th (allocation day)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Email Reminders</Label>
                <p className="text-sm text-muted-foreground">Receive reminders at {user?.email}</p>
              </div>
              <Switch
                checked={reminderPrefs.email_enabled}
                onCheckedChange={(checked) => setReminderPrefs({...reminderPrefs, email_enabled: checked})}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">SMS Reminders</Label>
                <p className="text-sm text-muted-foreground">Receive text message reminders</p>
              </div>
              <Switch
                checked={reminderPrefs.sms_enabled}
                onCheckedChange={(checked) => setReminderPrefs({...reminderPrefs, sms_enabled: checked})}
              />
            </div>
            
            {reminderPrefs.sms_enabled && (
              <div className="space-y-3 pl-4 border-l-2 border-[#1e3a5f]/20">
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={reminderPrefs.phone_number || ""}
                    onChange={(e) => setReminderPrefs({...reminderPrefs, phone_number: e.target.value})}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Include country code (e.g., +1 for US)</p>
                </div>
                <div className="p-3 rounded-lg bg-stone-50 border border-stone-200">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reminderPrefs.sms_consent || false}
                      onChange={(e) => setReminderPrefs({...reminderPrefs, sms_consent: e.target.checked})}
                      className="mt-0.5 rounded border-stone-300"
                      data-testid="sms-consent-checkbox"
                    />
                    <span className="text-xs text-stone-600">
                      I agree to receive recurring automated text messages from Opturna Tax AI at the phone number provided. Message frequency varies (2-4/month). Message & data rates may apply. Reply STOP to opt out anytime. See our{" "}
                      <a href="/sms-terms" target="_blank" className="text-[#1e3a5f] underline font-medium">SMS Terms & Conditions</a>{" "}
                      and{" "}
                      <a href="/privacy" target="_blank" className="text-[#1e3a5f] underline font-medium">Privacy Policy</a>.
                    </span>
                  </label>
                </div>
              </div>
            )}
            
            <Separator />
            
            <div className="space-y-3">
              <Label className="font-medium">Reminder Schedule</Label>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm">Day Before (9th/24th)</p>
                  <p className="text-xs text-muted-foreground">Prepare for tomorrow's allocation</p>
                </div>
                <Switch
                  checked={reminderPrefs.reminder_day_before}
                  onCheckedChange={(checked) => setReminderPrefs({...reminderPrefs, reminder_day_before: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm">Morning Of (10th/25th)</p>
                  <p className="text-xs text-muted-foreground">Time to run your allocation!</p>
                </div>
                <Switch
                  checked={reminderPrefs.reminder_morning_of}
                  onCheckedChange={(checked) => setReminderPrefs({...reminderPrefs, reminder_morning_of: checked})}
                />
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSaveReminders}
              disabled={savingReminders}
              className="bg-[#1e3a5f] hover:bg-[#2d5a8e]"
            >
              {savingReminders ? "Saving..." : "Save Preferences"}
            </Button>
            <Button
              variant="outline"
              onClick={handleTestReminders}
              disabled={testingReminders}
            >
              {testingReminders ? "Sending..." : "Send Test Reminder"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Connected Accounts / Disconnect Bank */}
      <Card data-testid="connected-accounts-card">
        <CardHeader>
          <CardTitle>Connected Accounts</CardTitle>
          <CardDescription>Manage your linked bank connections via Plaid.</CardDescription>
        </CardHeader>
        <CardContent>
          {connectedBanks.length === 0 ? (
            <p className="text-sm text-stone-500 py-4 text-center">No bank accounts currently connected via Plaid.</p>
          ) : (
            <div className="space-y-3">
              {connectedBanks.map((bank) => (
                <div key={bank.id} className="flex items-center justify-between p-4 rounded-xl border border-stone-200">
                  <div>
                    <p className="font-medium text-stone-900">{bank.institution_name || "Connected Institution"}</p>
                    <p className="text-xs text-stone-500">{bank.account_type || "Bank Account"} &middot; Last synced: {bank.last_synced ? new Date(bank.last_synced).toLocaleDateString() : "N/A"}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => { setDisconnectingBank(bank); setShowDisconnectDialog(true); }}
                    data-testid={`disconnect-bank-${bank.id}`}
                  >
                    Disconnect
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Account */}
      <Card className="border-red-200" data-testid="delete-account-card">
        <CardHeader>
          <CardTitle className="text-red-700">Delete Account</CardTitle>
          <CardDescription>Permanently delete your Opturna account and all associated data.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 mb-4">
            <p className="text-sm text-red-800 font-medium mb-2">This action is permanent and cannot be undone.</p>
            <ul className="text-xs text-red-700 space-y-1 list-disc ml-4">
              <li>All linked bank connections will be disconnected</li>
              <li>Access to reports, transactions, and financial data will end</li>
              <li>Production data will be deleted within 30 days</li>
              <li>Backup copies will be purged within 60 days</li>
              <li>Some limited records may be kept for legal or fraud-prevention purposes</li>
            </ul>
          </div>
          <Button
            variant="destructive"
            onClick={() => { setShowDeleteAccount(true); setDeleteStep(1); setDeletePassword(""); setDeleteReason(""); }}
            data-testid="delete-account-btn"
          >
            Delete My Account
          </Button>
        </CardContent>
      </Card>

      {/* Disconnect Bank Confirmation Dialog */}
      <Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <DialogContent data-testid="disconnect-bank-dialog">
          <DialogHeader>
            <DialogTitle>Disconnect Bank</DialogTitle>
            <DialogDescription>
              Disconnecting {disconnectingBank?.institution_name || "this bank"} will stop future balance and transaction updates from this institution.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
            <p className="font-medium mb-1">What happens when you disconnect:</p>
            <ul className="list-disc ml-4 space-y-1 text-xs">
              <li>Future imports, balance refreshes, and transaction syncs will stop</li>
              <li>Your Opturna account will remain active</li>
              <li>Historical data already imported may remain in your account unless you separately request deletion</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisconnectDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDisconnectBank}
              disabled={disconnecting}
              data-testid="confirm-disconnect-btn"
            >
              {disconnecting ? "Disconnecting..." : "Disconnect Bank"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteAccount} onOpenChange={setShowDeleteAccount}>
        <DialogContent className="max-w-md" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete Account</DialogTitle>
            <DialogDescription>
              {deleteStep === 1 ? "Please review what will happen when you delete your account." : "Confirm your identity to proceed with deletion."}
            </DialogDescription>
          </DialogHeader>

          {deleteStep === 1 && (
            <div className="space-y-4">
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-200 text-sm">
                <p className="font-medium text-stone-900 mb-2">Deleting your account will permanently:</p>
                <ul className="list-disc ml-4 space-y-1 text-xs text-stone-600">
                  <li>Remove your Opturna profile and most associated data</li>
                  <li>Disconnect all linked bank connections</li>
                  <li>Delete all businesses, transactions, and reports</li>
                  <li>Cancel any active subscription</li>
                </ul>
                <p className="mt-3 text-xs text-stone-500">
                  Production data deletion will be completed within 30 days. Backup copies will be purged within 60 days. Some limited records may be kept where required for legal, security, or fraud-prevention purposes.
                </p>
              </div>
              <div>
                <Label className="text-xs text-stone-500">Reason for leaving (optional)</Label>
                <Select value={deleteReason} onValueChange={setDeleteReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_useful">Not useful for my business</SelectItem>
                    <SelectItem value="too_expensive">Too expensive</SelectItem>
                    <SelectItem value="switching">Switching to another tool</SelectItem>
                    <SelectItem value="privacy">Privacy concerns</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteAccount(false)}>Cancel</Button>
                <Button variant="destructive" onClick={() => setDeleteStep(2)} data-testid="delete-step2-btn">
                  Continue
                </Button>
              </DialogFooter>
            </div>
          )}

          {deleteStep === 2 && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-800 font-medium">Confirm your password to delete your account.</p>
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  data-testid="delete-password-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteStep(1)}>Back</Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount || !deletePassword}
                  data-testid="confirm-delete-btn"
                >
                  {deletingAccount ? "Deleting..." : "Delete My Account"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* About */}
      <Card>
        <CardHeader>
          <CardTitle>About Opturna Tax AI&#8482;</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-stone-600">
            Opturna Tax&#8482; incorporates principles inspired by the Profit First&#174; methodology created by Mike Michalowicz. The core concept shifts the traditional formula from:
          </p>
          <div className="flex flex-col items-center gap-2 py-2">
            <p className="text-stone-500 font-medium">Sales - Expenses = Profit</p>
            <p className="text-sm text-stone-400">to a profit-first approach:</p>
            <p className="text-[#1e3a5f] font-bold text-lg">Sales - Profit = Expenses</p>
          </div>
          <p className="text-stone-600">
            By prioritizing profit allocation, Opturna helps businesses maintain profitability and improve cash flow management regardless of revenue fluctuations.
          </p>
          <div className="p-4 rounded-xl bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
            <h4 className="font-semibold text-[#1e3a5f] mb-3">The Five Profit Allocation Accounts</h4>
            <p className="text-sm text-stone-500 mb-3">Opturna Tax AI&#8482; supports a five-account structure inspired by Profit First&#174;:</p>
            <ul className="space-y-2 text-sm text-[#1e3a5f]">
              <li><strong>Income:</strong> Revenue Hub — All money enters here first</li>
              <li><strong>Profit:</strong> Your reward for running the business (5–15%)</li>
              <li><strong>Owner Pay:</strong> Your consistent salary (30–50%)</li>
              <li><strong>Tax:</strong> Set aside for quarterly tax payments (15–30%)</li>
              <li><strong>Operating Expense:</strong> Everything needed to run the business (30–50%)</li>
            </ul>
          </div>
          <p className="text-xs text-stone-400 italic">
            Disclaimer: Profit First&#174; is a registered trademark of Mike Michalowicz. Opturna Tax AI&#8482; is not affiliated with or endorsed by Mike Michalowicz.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
